/**
 * Foundational types and constants for the Galaxy Ranger engine.
 *
 * Numeric constants are derived from the Ghidra-decompiled original
 * (see /docs/MECHANICS.md). Mechanics — costs, ranges, sight, scoring —
 * are facts about the original system rather than original creative work,
 * and are reproduced here so the recreation matches the original feel.
 */

/** Grid edge length. The original board is 100×100 cells. */
export const BOARD_SIZE = 100;

/**
 * Unit class IDs, matching the original game's internal class numbering
 * (confirmed via FUN_00407290 in the decomp). The order is also the
 * factory's produce-key order 0x22..0x29.
 */
export enum UnitKind {
  Worker = 1,
  Truck = 2,
  Soldier = 3,
  ElectronicGun = 4,
  RocketPlant = 5,
  SpaceTank = 6,
  SpaceScout = 7,
  SpaceCruiser = 8,
}

export const ALL_KINDS: readonly UnitKind[] = [
  UnitKind.Worker,
  UnitKind.Truck,
  UnitKind.Soldier,
  UnitKind.ElectronicGun,
  UnitKind.RocketPlant,
  UnitKind.SpaceTank,
  UnitKind.SpaceScout,
  UnitKind.SpaceCruiser,
] as const;

/**
 * Movable units — the ones that step on the grid each tick.
 * Static units (electronic gun, rocket plant) hold position once placed.
 */
export const MOVABLE_KINDS: ReadonlySet<UnitKind> = new Set([
  UnitKind.Worker,
  UnitKind.Truck,
  UnitKind.Soldier,
  UnitKind.SpaceTank,
  UnitKind.SpaceScout,
  UnitKind.SpaceCruiser,
]);

/** Static (placed, no movement) units. */
export const STATIC_KINDS: ReadonlySet<UnitKind> = new Set([
  UnitKind.ElectronicGun,
  UnitKind.RocketPlant,
]);

/**
 * Eight-way movement directions, numbered 1..8 matching the original
 * direction table at `FUN_00410be0:28-53`. We expose readable names
 * and a vector table for translation.
 */
export enum Direction {
  North = 1,
  NorthEast = 2,
  East = 3,
  SouthEast = 4,
  South = 5,
  SouthWest = 6,
  West = 7,
  NorthWest = 8,
}

export const DIRECTION_VECTORS: Readonly<Record<Direction, readonly [number, number]>> = {
  [Direction.North]: [0, 1],
  [Direction.NorthEast]: [1, 1],
  [Direction.East]: [1, 0],
  [Direction.SouthEast]: [1, -1],
  [Direction.South]: [0, -1],
  [Direction.SouthWest]: [-1, -1],
  [Direction.West]: [-1, 0],
  [Direction.NorthWest]: [-1, 1],
} as const;

/**
 * Difficulty levels.
 *  1 = easy, 2 = normal, 3 = hard, 4 = super, 5 = insane,
 *  6 = online (forced when playing against a human partner).
 */
export enum Difficulty {
  Easy = 1,
  Normal = 2,
  Hard = 3,
  Super = 4,
  Insane = 5,
  Online = 6,
}

/** Players in a session. */
export enum PlayerId {
  Human = 0,
  Opponent = 1,
}

/** Two-coord grid position. */
export interface Position {
  readonly x: number;
  readonly y: number;
}

/**
 * Chebyshev distance — the original uses this for all sight/range/proximity
 * checks (8-way scan boxes around units).
 */
export function chebyshev(a: Position, b: Position): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** True iff a position is inside the playable board. */
export function isOnBoard(p: Position): boolean {
  return p.x >= 0 && p.x < BOARD_SIZE && p.y >= 0 && p.y < BOARD_SIZE;
}
